import type { AvailableLanguages } from './Project.svelte'

export const PAGE_SIZE = 16 * 16
export const PAGE_ELEMENTS_PER_ROW = Math.sqrt(PAGE_SIZE)

/**
 * Keep source-map connections this far beyond each editor's top and bottom edges,
 * measured as a fraction of its viewport height. 0 clips immediately; 0.5 keeps
 * connections for half a viewport offscreen; 1 keeps them for a full viewport.
 */
export const SOURCE_MAP_CONNECTION_OFFSCREEN_RATIO = 0.8

/** Choose 'curved' for smooth ribbons or 'straight' for direct connections with gentle end bends. */
export const SOURCE_MAP_CONNECTION_STYLE: 'curved' | 'straight' = 'straight'

/** Matching opacity for source/assembly section backgrounds and their connections. */
//export const SOURCE_MAP_COLOR_OPACITY = { default: 0.18, active: 0.18, dimmed: 0.04 } as const
export const SOURCE_MAP_COLOR_OPACITY = { default: 0.18, active: 0.18, dimmed: 0.04 } as const

/** Hue step in degrees; saturation and lightness in percent (0-100). */
export const SOURCE_MAP_COLOR_PALETTE = { hueStep: 43, saturation: 45, lightness: 62 } as const

/** Border width in pixels (0 disables it); opacity is relative to the section background. */
export const SOURCE_MAP_SECTION_BORDER = { width: 1, opacity: 0.5 } as const

export const MEMORY_SIZE = {
    M68K: 0xffffffn,
    MIPS: 0xffffffffn,
    X86: 0x00007fffffffffffn,
    'RISC-V': 0xffffffffn,
    'RISC-V-64': 0xffffffffn,
    Z80: 0xffffn
} satisfies Record<AvailableLanguages, bigint>

export const DEFAULT_MEMORY_VALUE = {
    M68K: 0xff,
    MIPS: 0x00,
    X86: 0x00,
    'RISC-V': 0x00,
    'RISC-V-64': 0x00,
    Z80: 0x00
} satisfies Record<AvailableLanguages, number>

export const COMMENT_CHARACTER = {
    M68K: ';',
    MIPS: '#',
    X86: ';',
    'RISC-V': '#',
    'RISC-V-64': '#',
    Z80: ';'
} satisfies Record<AvailableLanguages, string>

/**
 * The empty project every language starts from. Each one ends by asking its environment to
 * stop, because that is the single thing a first program cannot be read from: falling off the
 * end of the text segment is not an ending, it is the assembler's next bytes being executed.
 * Nothing else here does any work, so the reader has one correct line to keep and a blank
 * space to write in, rather than a magic constant to wonder about.
 */
export const BASE_CODE = {
    MIPS: `
.data
    # Write your data here

.text
.globl main
main:
    # Write your code here

    li $v0, 10          # service 10: end the program
    syscall
`.trim(),
    M68K: `
ORG $1000

START:
    ; Write your code here

    move.b #9, d0       ; task 9: end the program
    trap #15
`.trim(),
    X86: `
global _start

section .data
    ; Write your data here

section .text
_start:
    ; Write your code here

    mov rax, 60         ; syscall 60: exit
    xor rdi, rdi        ; with status 0
    syscall
`.trim(),
    'RISC-V': `
.data
    # Write your data here

.text
.globl main
main:
    # Write your code here

    li a7, 10           # service 10: end the program
    ecall
`.trim(),
    'RISC-V-64': `
.data
    # Write your data here

.text
.globl main
main:
    # Write your code here

    li a7, 10           # service 10: end the program
    ecall
`.trim(),
    //not `.trim()`ed like the others: the leading indentation is load bearing, an assembler
    //directive in the first column is parsed as a label definition
    Z80: `        .org 0x8000
start:
        ; Write your code here

        halt`
} satisfies Record<AvailableLanguages, string>

export const LANGUAGE_THEMES = {
    M68K: 'default',
    MIPS: 'default-mips',
    X86: 'default-x86',
    'RISC-V': 'default-risc-v',
    'RISC-V-64': 'default-risc-v',
    Z80: 'default-z80'
} satisfies Record<AvailableLanguages, string>

export const LANGUAGE_EXTENSIONS = {
    M68K: 'm68k',
    MIPS: 'mips',
    X86: 'asm',
    'RISC-V': 'riscv',
    'RISC-V-64': 'riscv',
    Z80: 'z80'
} satisfies Record<AvailableLanguages, string>

export const DISCERNS_AVATAR_ID = 65
export const DISCERNS_AVATAR_INSTANCE_ID = 75

/**
 * The halt limit a Testcase run is given. Unlike a Run, a Testcase runs unsliced — the Core call
 * returns only when the program ends — so nothing outside it can answer Pause or Stop, and this
 * limit is the only thing that ends a testcase that loops forever. A Run needs no limit: it is
 * sliced ([ADR 0007](../../../../docs/adr/0007-generic-emulator-run-scheduling.md)) and the user pauses it.
 */
export const TESTCASE_INSTRUCTION_LIMIT = 2_000_000

/**
 * How far a Build or a Testcase of an Executable runs to reach `main` before it starts
 * ([binary-import.md](../../../../docs/design/binary-import.md), decision 11): the dynamic linker
 * and the start code of a C++ program run about 1.8 million instructions before `main` with
 * libstdc++, so `TESTCASE_INSTRUCTION_LIMIT` counts from `main`, and this generous cap stops a
 * program that never gets there. A Testcase whose program does not reach `main` within it fails
 * saying so; a Build stops where it got to.
 */
export const PROGRAM_START_INSTRUCTION_LIMIT = 50_000_000
